import { AnalysisRun, AnalysisRunService } from "./analysis-run.service";
import { Injectable, Injector } from "@angular/core";
import { Evaluator } from "../evaluators/evaluator";
import {
  Constants,
  EvaluationFlowsEnum,
  ICondition,
  IEvaluationStepsConfig,
  IMultipleConditions,
  Node,
  ResultInfo
} from "../models/shared.model";
import { IEvaluator } from "../interfaces/evaluator-interface";
import { AttachmentService } from "./attachment.service";
import { LinksService } from "./links.service";
import { SharedService } from "./shared.service";
import { LogService } from "./log.service";
import { RiskAssessmentService } from "./risk-assessment.service";
import { RiskAssessment } from "../models/risk-assessment";
import { FlowNodeNamesEnum } from "../models/flow";
import { DomainAgeAnalyzer } from "../analyzers";
import { RiskLevel } from "../models/risk-level";
import {
  DOMAIN_CONFIG_TO_FLOW_STEP,
  DOMAIN_CONFIG_TO_RESULT_INFO,
  DOMAIN_CONFIG_VALUES,
  DOMAIN_ORIGIN_CONDITION_VALUES,
  NodeResultDecisions
} from "../models/constants";
import { StepResult } from "../models/step-result";
import { WaitService } from "./wait.service";
import { ImageService } from "./image.service";
import { QRCodeService } from "./qrCode.service";

@Injectable({
  providedIn: "root"
})
export class EvaluatorService {

  private evaluators: Array<IEvaluator> = [];
  private _evaluatorsScoreMap: Map<string, number> = new Map();
  private _assessment: RiskAssessment = null;
  private _multipleConditions: IMultipleConditions[] = [];
  private _needleValue = -1;

  constructor(
    private injector: Injector,
    private runs: AnalysisRunService,
    private attachmentService: AttachmentService,
    private linkService: LinksService,
    private sharedService: SharedService,
    private logService: LogService,
    private riskService: RiskAssessmentService,
    private waitService: WaitService,
    private imageService: ImageService,
    private qrCodeService: QRCodeService
  ) {

    this.sharedService.messageReceived.subscribe(msg => {
      const run = this.runs.current;
      if (!run?.active || msg.runId !== run.id) return;
      const evaluator = this.evaluators.find(value => value.getType() === msg.type &&
        value.getFlow()?.getNode(msg.node)?.logId === msg.nodeLogId);
      const node = evaluator?.getFlow()?.getNode(msg.node);
      const path = node?.nodePaths[msg.result];
      if (!path) return;
      const points = path.points;
      if (msg.result !== NodeResultDecisions.PENDING) run.resolveIssue(msg.type, `node:${node.logId}`);

      this.updateNode(node, msg.result, evaluator.getType());
      this.updateFlow(evaluator, msg.type, msg.info, true);

      this.logService.updateLog(node.logId, msg.reasonLog, msg.result, points.value);
    });

    this.waitService.resetData.subscribe((reset) => {
      if (reset) {
        this.resetData();
      }
    });
  }

  get evaluatorsScoreMap(): Map<string, number> {
    return this._evaluatorsScoreMap;
  }

  set assessment(value: RiskAssessment) {
    this._assessment = value;
  }

  set multipleConditions(value: IMultipleConditions[]) {
    this._multipleConditions = value;
  }

  get needleValue(): number {
    return this._needleValue;
  }

  private resetData() {
    this.evaluators = [];
    this._evaluatorsScoreMap = new Map();
    this._assessment = null;
    this._multipleConditions = [];
    this._needleValue = -1;
  }

  private findFlow(evaluationFlow: IEvaluationStepsConfig[], flowName: EvaluationFlowsEnum){
    return  evaluationFlow.find(flow => flow.name == flowName);
  }
  
  public async initSenderEvaluators(evaluationFlow: IEvaluationStepsConfig[], run: AnalysisRun = this.runs.current) {
    run.assertActive();

    const senderFLows = Object.keys(EvaluationFlowsEnum).filter(key =>
      EvaluationFlowsEnum[key] == EvaluationFlowsEnum.SENDER || EvaluationFlowsEnum[key] == EvaluationFlowsEnum.IMPERSONATION_RISK);

    let temp: Array<IEvaluator> = [];

    for (const key of senderFLows) {
      let enumValue = EvaluationFlowsEnum[key];
      let flow = this.findFlow(evaluationFlow, enumValue);
      if (!flow)
        continue;

      const ev = new Evaluator(this.injector, enumValue, flow);
      temp.push(ev);

      this._evaluatorsScoreMap.set(enumValue, 0);
    }

    run.assertActive();
    this.evaluators = [...this.evaluators, ...temp];

    return temp;
  }

  public async initAttachmentEvaluators(evaluationFlow: IEvaluationStepsConfig[], run: AnalysisRun = this.runs.current) {
    run.assertActive();
    let enumValue = EvaluationFlowsEnum.ATTACHMENTS;

    let flow = this.findFlow(evaluationFlow, enumValue);

    if (!flow)
      return [];

    const attachmentFlow = this.attachmentService.attachmentsFlow(enumValue, flow, run);
    run.assertActive();
    this.evaluators = [...this.evaluators, ...attachmentFlow];

    this._evaluatorsScoreMap.set(enumValue, 0);

    return attachmentFlow;
  }

  public async initLinksEvaluators(evaluationFlow: IEvaluationStepsConfig[], run: AnalysisRun = this.runs.current) {
    run.assertActive();
    let enumValue = EvaluationFlowsEnum.LINKS;

    let flow = this.findFlow(evaluationFlow, enumValue);

    if (!flow)
      return [];

    const linksFlow = await this.linkService.linksFlow(enumValue, flow, run);
    run.assertActive();
    this.evaluators = [...this.evaluators, ...linksFlow];

    this._evaluatorsScoreMap.set(enumValue, 0);

    return linksFlow;
  }

  public async initQRCodeEvaluators(evaluationFlow: IEvaluationStepsConfig[], run: AnalysisRun = this.runs.current) {
    run.assertActive();
    let enumValue = EvaluationFlowsEnum.QRCODE;

    let flow = this.findFlow(evaluationFlow, enumValue);

    if (!flow)
      return [];

    const qrCodeFlow = await this.qrCodeService.qrCodeFlow(enumValue, flow, run);
    run.assertActive();
    this.evaluators = [...this.evaluators, ...qrCodeFlow];

    this._evaluatorsScoreMap.set(enumValue, 0);

    return qrCodeFlow;
  }


  public updateNode(node: Node, decision: any, flowType: string) {
    node.result = {
      decision: decision,
      points: node.nodePaths[decision].points.value,
      overviewText: { ...node.nodePaths[decision].overviewText, flow: flowType }
    };
  }

  public updateFlow(evaluator: IEvaluator, flowType: string, info: ResultInfo, forceSetFlowScore: boolean = false) {
    this._assessment?.updateAnalysisResultInfoEntry(flowType, info);

    this.riskService.setScoring(evaluator, evaluator.getType(), forceSetFlowScore);

    this.updateTotalScore();
  }

  updateTotalScore() {
    this._needleValue = 0;

    this.riskService.evaluatorsScoreMap.forEach(value => {
      this._needleValue += value;
    });

    this._needleValue += this.applyMultipleConditions();

    if (this._needleValue > 100)
      this._needleValue = 100;

    this.riskService.resetOverviewMap();

    this.evaluators.forEach(evaluator => {
      this.riskService.setOverviewMap(evaluator.getFlow());
    });

    this.riskService.setMultipleConditionsOverview(this._multipleConditions)
  }

  applyMultipleConditions() {
    let totalPoints = 0;
    this._multipleConditions?.forEach(conditionObj => {
      let expression = conditionObj.expression || this.constructAndingExpression(conditionObj);
      let logMsg = expression;
      conditionObj.conditions.forEach(condition => {
        let res = this.evaluateCondition(condition);
        logMsg = logMsg.replaceAll(condition.key, `${condition.stepId} = ${condition.value}`);
        expression = expression.replaceAll(condition.key, res ? "1" : "0");
      });

      // Add points if the expression is true
      if (eval(expression)) {
        totalPoints += conditionObj.points;
        this.logService.multipleConditionsLog(conditionObj.title, conditionObj.points, logMsg);
        conditionObj.result = {
          points: conditionObj.points,
          decision: "true",
          overviewText: conditionObj.overviewText
        };
      }
      else if (conditionObj.result) {
        conditionObj.result.decision = "false"
      }
    });

    this.riskService.evaluatorsScoreMap.set(Constants.CONDITIONS_GROUP, totalPoints);

    return totalPoints;
  }

  constructAndingExpression(conditionObj: IMultipleConditions): string {
    let expression = "";
    conditionObj.conditions.forEach((cond, index) => {
      cond.key = "cond" + (index + 1);
      if (index != 0)
        expression += " && ";
      expression += cond.key;

    });
    return expression;
  }

  evaluateCondition(condition: ICondition) {
    let evaluators = this.evaluators.filter(ev => ev.getType() === condition.flowName);

    let conditionStepId = condition.stepId;
    let conditionValue: any = condition.value;

    if(condition.flowName == Constants.DOMAIN_CONFIG_CONDITION)
      return this.evaluateDomainConfigCondition(condition);

    if(condition.flowName == Constants.DOMAIN_ORIGIN_CONDITION)
      this.evaluateDomainOriginCondition(condition);

    // Check if at least one evaluator meets the condition
    return evaluators.some(ev => {
      const node = ev.getFlow().getNode(conditionStepId);
      return node?.result?.decision?.toString() === conditionValue;
    });
  }

  /**
   * In case of DOMAIN_CONFIG_CONDITION Condition we are interested in IMPERSONATION_RISK flow only.
   * We convert the condition stepId to a node using DOMAIN_CONFIG_TO_FLOW_STEP enum,
   * and we get the value from the node resultInfo we get the resultInfo key using this enum DOMAIN_CONFIG_TO_RESULT_INFO
   */
  evaluateDomainConfigCondition(condition: ICondition){
    let evaluators = this.evaluators.filter(value => value.getType() == EvaluationFlowsEnum.IMPERSONATION_RISK)

    let conditionStepId = DOMAIN_CONFIG_TO_FLOW_STEP[condition.stepId];
    let conditionValue = condition.value == DOMAIN_CONFIG_VALUES.SETUP;

    return evaluators.some(ev => {
      const node = ev.getFlow().getNode(conditionStepId);
      return node?.result?.info?.[DOMAIN_CONFIG_TO_RESULT_INFO[condition.stepId]] === conditionValue;
    });
  }

  /**
   * In case of DOMAIN_ORIGIN_CONDITION Condition we are interested in SENDER flow only.
   * We get the node of IsSenderDomainTrusted,
   * and we get the value from the node resultInfo we get the resultInfo key using this enum DOMAIN_CONFIG_TO_RESULT_INFO
   */
  evaluateDomainOriginCondition(condition: ICondition){
    let evaluators = this.evaluators.filter(value => value.getType() == EvaluationFlowsEnum.SENDER)

    let conditionStepId = FlowNodeNamesEnum.isSenderDomainTrusted;
    let conditionValue = condition.value == DOMAIN_ORIGIN_CONDITION_VALUES.MATCH;
    return evaluators.some(ev => {
      const node = ev.getFlow().getNode(conditionStepId);

      const smtpOrigin = node?.result?.info?.['smtpOrigin'];
      const smtpOriginMatchSender = node?.result?.info?.['smtpOriginMatchSender']

      switch (condition.value) {
        case DOMAIN_ORIGIN_CONDITION_VALUES.NOT_PROVIDED:
          return smtpOrigin === Constants.NOT_PROVIDED;

        case DOMAIN_ORIGIN_CONDITION_VALUES.MATCH:
          return smtpOrigin !== Constants.NOT_PROVIDED && smtpOriginMatchSender;

        case DOMAIN_ORIGIN_CONDITION_VALUES.DOESNT_MATCH:
          return smtpOrigin == Constants.NOT_PROVIDED || !smtpOriginMatchSender;

        default:
          return false;
      }
    });
  }

  async checkAge() {

    let evaluator = this.evaluators.find(value => value.getType() == EvaluationFlowsEnum.SENDER);

    let ageNode = evaluator?.getFlow()?.getNode(FlowNodeNamesEnum.getDomainAge);

    let domainAgeAnalyzer = new DomainAgeAnalyzer(this.injector);
    let res = await domainAgeAnalyzer.runAnalysis();
    let age = res.level == RiskLevel.Low ? NodeResultDecisions.OLD :
      (res.level == RiskLevel.Moderate ? NodeResultDecisions.PENDING : NodeResultDecisions.NEW);


    this.updateNode(ageNode, age, evaluator.getType());
    this.updateFlow(evaluator, EvaluationFlowsEnum.SENDER, { ...res.additionalInfo });

    let stepResult = new StepResult(age, res.additionalInfo.info, res.additionalInfo);

    this.logService.updateLog(ageNode.logId, stepResult.reasonLog, stepResult.result, ageNode.nodePaths[age].points.value, ageNode, evaluator.getType());
  }
}
